import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from '../ui/sidebar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { Logo } from '../ui/logo'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Badge } from '../ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import {
  LogOut,
  UserCog,
  Car,
  MessageCircle,
  ExternalLink,
  ChevronRight,
} from 'lucide-react'
import { UserRole } from '../data/orderWorkflowData'
import {
  getMenuItemsForRole,
  NavigationService,
  type MenuItemConfig,
  type MenuGroupId,
  type ResolvedMenuGroup,
  type SystemSubGroupId,
} from '../../config/routes'

// ============= Feature Flag =============

const GROUPED_NAV_STORAGE_KEY = 'sentiflor:grouped_nav_enabled'
const EXPANDED_GROUPS_STORAGE_KEY = 'sentiflor:expanded_groups'

/**
 * Get grouped navigation feature flag
 * Priority: server override > localStorage > default (true for new UI)
 */
function getGroupedNavEnabled(): boolean {
  // Check for server override via env variable
  const serverOverride = import.meta.env.VITE_FORCE_GROUPED_NAV
  if (serverOverride === 'true') return true
  if (serverOverride === 'false') return false

  // Check localStorage
  try {
    const stored = localStorage.getItem(GROUPED_NAV_STORAGE_KEY)
    if (stored !== null) return stored === 'true'
  } catch {
    // localStorage not available
  }

  // Default: enable grouped navigation
  return true
}

/**
 * Save grouped navigation preference
 */
function setGroupedNavEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(GROUPED_NAV_STORAGE_KEY, String(enabled))
  } catch {
    // localStorage not available
  }
}

// ============= Expanded Groups Persistence =============

/**
 * Load expanded groups state from localStorage
 */
function loadExpandedGroups(): Record<MenuGroupId, boolean> {
  try {
    const stored = localStorage.getItem(EXPANDED_GROUPS_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // Parse error or localStorage not available
  }
  return NavigationService.getDefaultExpandedGroups()
}

/**
 * Save expanded groups state to localStorage
 */
function saveExpandedGroups(expanded: Record<MenuGroupId, boolean>): void {
  try {
    localStorage.setItem(EXPANDED_GROUPS_STORAGE_KEY, JSON.stringify(expanded))
  } catch {
    // localStorage not available
  }
}

interface AppSidebarProps {
  currentPage: string
  onPageChange: (page: string) => void
  userRole?: UserRole
  onRoleChange?: (role: UserRole) => void
  badgeCounts?: Record<string, number | undefined>
}

const roleLabels: Record<UserRole, string> = {
  admin: 'Administrator',
  dispatcher: 'Dispetçer',
  'manager-florist': 'Baş florist',
  florist: 'Florist',
  'delivery-manager': 'Çatdırılma meneceri',
  courier: 'Kuryer',
  support: 'Dəstək',
}

// ============= Menu Item Component (Extract Component - Fowler) =============

interface MenuItemProps {
  item: MenuItemConfig
  isActive: boolean
  onNavigate: (id: string, path: string) => void
  /** Whether this item is nested inside a group (smaller styling) */
  isNested?: boolean
}

function MenuItem({ item, isActive, onNavigate, isNested = false }: MenuItemProps) {
  const IconComponent = item.icon
  const isExternalLink = item.path.startsWith('http')

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => {
          if (isExternalLink) {
            window.open(item.path, '_blank')
          } else {
            onNavigate(item.id, item.path)
          }
        }}
        isActive={isActive}
        className={`
          w-full flex items-center gap-3 rounded-lg transition-colors
          data-[active=true]:!bg-[var(--color-delivery)] data-[active=true]:!text-white
          ${isNested
            ? 'px-3 py-2 text-sm hover:bg-gray-50'
            : 'px-4 py-3 hover:bg-gray-100'
          }
        `}
      >
        <IconComponent className={isNested ? 'w-4 h-4' : 'w-5 h-5'} />
        <span className="flex-1">{item.label}</span>
        {isExternalLink && <ExternalLink className="w-4 h-4 opacity-50" />}
        {item.badge && item.badge > 0 && (
          <Badge
            variant="secondary"
            className={`ml-auto text-xs ${
              isActive ? 'bg-white/20 text-white' : 'bg-admin-accent text-white'
            }`}
          >
            {item.badge}
          </Badge>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

// ============= Grouped Navigation Component =============

interface GroupedNavigationProps {
  groups: ResolvedMenuGroup[]
  currentPage: string
  expandedGroups: Record<MenuGroupId, boolean>
  expandedSubGroups: Record<SystemSubGroupId, boolean>
  onToggleGroup: (groupId: MenuGroupId) => void
  onToggleSubGroup: (subGroupId: SystemSubGroupId) => void
  onNavigate: (id: string, path: string) => void
}

function GroupedNavigation({
  groups,
  currentPage,
  expandedGroups,
  expandedSubGroups,
  onToggleGroup,
  onToggleSubGroup,
  onNavigate,
}: GroupedNavigationProps) {
  // Find which group/subgroup contains current page for highlighting
  const currentRouteInfo = useMemo(
    () => NavigationService.findGroupForRoute(currentPage),
    [currentPage]
  )

  return (
    <SidebarMenu className="space-y-1">
      {groups.map((group, groupIndex) => {
        const GroupIcon = group.icon
        const isGroupActive = currentRouteInfo?.groupId === group.id
        const isExpanded = expandedGroups[group.id] ?? false
        const isFirstGroup = groupIndex === 0

        return (
          <SidebarGroup key={group.id} className="p-0">
            {/* Separator between groups (except first) */}
            {!isFirstGroup && (
              <div className="mx-3 my-2 border-t border-gray-100" />
            )}

            <Collapsible open={isExpanded} onOpenChange={() => onToggleGroup(group.id)}>
              {/* Group Header - visually distinct from items */}
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel
                  className={`
                    flex items-center gap-2.5 px-3 py-2.5 cursor-pointer rounded-lg
                    transition-all duration-200 select-none
                    text-gray-700 uppercase text-xs tracking-wider font-semibold
                    hover:bg-gray-100/80
                    ${isGroupActive && !isExpanded ? 'bg-gray-100/60' : ''}
                    ${isExpanded ? 'bg-gray-50/50' : ''}
                  `}
                  role="button"
                  aria-expanded={isExpanded}
                  aria-controls={`group-content-${group.id}`}
                  tabIndex={0}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onToggleGroup(group.id)
                    }
                  }}
                >
                  <ChevronRight
                    className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
                      isExpanded ? 'rotate-90' : ''
                    }`}
                  />
                  <GroupIcon className="w-4 h-4 text-gray-500" />
                  <span className="flex-1">{group.label}</span>
                  {group.totalBadge > 0 && (
                    <Badge
                      variant="secondary"
                      className="bg-admin-accent text-white text-[11px] px-1.5 py-0 h-4 min-w-[18px] flex items-center justify-center"
                    >
                      {group.totalBadge}
                    </Badge>
                  )}
                </SidebarGroupLabel>
              </CollapsibleTrigger>

              {/* Group Content - indented with visual connector */}
              <CollapsibleContent id={`group-content-${group.id}`}>
                <SidebarGroupContent className="mt-1 ml-3 pl-3 border-l-2 border-gray-200/70">
                  {/* Main items - nested style */}
                  {group.items.map(item => (
                    <MenuItem
                      key={item.id}
                      item={item}
                      isActive={currentPage === item.id}
                      onNavigate={onNavigate}
                      isNested
                    />
                  ))}

                  {/* Subgroups - with distinct header */}
                  {group.subGroups?.map(subGroup => {
                    const SubGroupIcon = subGroup.icon
                    const isSubGroupExpanded = expandedSubGroups[subGroup.id] ?? false
                    const isSubGroupActive = currentRouteInfo?.subGroupId === subGroup.id

                    return (
                      <Collapsible
                        key={subGroup.id}
                        open={isSubGroupExpanded}
                        onOpenChange={() => onToggleSubGroup(subGroup.id)}
                      >
                        {/* Subgroup Header - distinct from items */}
                        <CollapsibleTrigger asChild>
                          <div
                            className={`
                              flex items-center gap-2 px-2.5 py-1.5 cursor-pointer rounded-md
                              transition-colors select-none mt-2 mb-1
                              text-[11px] uppercase tracking-wide font-medium text-gray-500
                              hover:bg-gray-100/60 hover:text-gray-600
                              ${isSubGroupActive && !isSubGroupExpanded ? 'bg-gray-100/40' : ''}
                            `}
                            role="button"
                            aria-expanded={isSubGroupExpanded}
                            tabIndex={0}
                            onKeyDown={(e: React.KeyboardEvent) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onToggleSubGroup(subGroup.id)
                              }
                            }}
                          >
                            <ChevronRight
                              className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${
                                isSubGroupExpanded ? 'rotate-90' : ''
                              }`}
                            />
                            <SubGroupIcon className="w-3.5 h-3.5" />
                            <span className="flex-1">{subGroup.label}</span>
                          </div>
                        </CollapsibleTrigger>

                        {/* Subgroup Items - deeper nesting */}
                        <CollapsibleContent>
                          <SidebarMenuSub className="ml-2 pl-2.5 border-l border-gray-200/50">
                            {subGroup.items.map(item => {
                              const ItemIcon = item.icon
                              const isExternalLink = item.path.startsWith('http')

                              return (
                                <SidebarMenuSubItem key={item.id}>
                                  <SidebarMenuSubButton
                                    onClick={() => {
                                      if (isExternalLink) {
                                        window.open(item.path, '_blank')
                                      } else {
                                        onNavigate(item.id, item.path)
                                      }
                                    }}
                                    isActive={currentPage === item.id}
                                    className={`
                                      flex items-center gap-2 py-1.5 text-xs
                                      ${currentPage === item.id
                                        ? '!bg-[var(--color-delivery)] !text-white'
                                        : 'text-gray-600 hover:text-gray-900'
                                      }
                                    `}
                                  >
                                    <ItemIcon className="w-3.5 h-3.5" />
                                    <span>{item.label}</span>
                                    {isExternalLink && (
                                      <ExternalLink className="w-3 h-3 opacity-50 ml-auto" />
                                    )}
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              )
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </Collapsible>
                    )
                  })}
                </SidebarGroupContent>
              </CollapsibleContent>
            </Collapsible>
          </SidebarGroup>
        )
      })}
    </SidebarMenu>
  )
}

// ============= Flat Navigation Component (Legacy) =============

interface FlatNavigationProps {
  items: MenuItemConfig[]
  currentPage: string
  onNavigate: (id: string, path: string) => void
}

function FlatNavigation({ items, currentPage, onNavigate }: FlatNavigationProps) {
  return (
    <SidebarMenu>
      {items.map(item => (
        <MenuItem
          key={item.id}
          item={item}
          isActive={currentPage === item.id}
          onNavigate={onNavigate}
        />
      ))}
    </SidebarMenu>
  )
}

export function AppSidebar({
  currentPage,
  onPageChange,
  userRole = 'admin',
  onRoleChange,
  badgeCounts,
}: AppSidebarProps) {
  const navigate = useNavigate()

  // Feature flag for grouped navigation
  const [useGroupedNav, setUseGroupedNav] = useState(() => getGroupedNavEnabled())

  // Expanded state for groups
  const [expandedGroups, setExpandedGroups] = useState<Record<MenuGroupId, boolean>>(() =>
    loadExpandedGroups()
  )

  // Expanded state for subgroups
  const [expandedSubGroups, setExpandedSubGroups] = useState<Record<SystemSubGroupId, boolean>>({
    storefront: false,
  })

  // Auto-expand group containing current page
  useEffect(() => {
    const routeInfo = NavigationService.findGroupForRoute(currentPage)
    if (routeInfo) {
      setExpandedGroups(prev => {
        if (prev[routeInfo.groupId]) return prev
        const updated = { ...prev, [routeInfo.groupId]: true }
        saveExpandedGroups(updated)
        return updated
      })

      if (routeInfo.subGroupId) {
        setExpandedSubGroups(prev => {
          if (prev[routeInfo.subGroupId!]) return prev
          return { ...prev, [routeInfo.subGroupId!]: true }
        })
      }
    }
  }, [currentPage])

  // Toggle group expanded state
  const handleToggleGroup = useCallback((groupId: MenuGroupId) => {
    setExpandedGroups(prev => {
      const updated = { ...prev, [groupId]: !prev[groupId] }
      saveExpandedGroups(updated)
      return updated
    })
  }, [])

  // Toggle subgroup expanded state
  const handleToggleSubGroup = useCallback((subGroupId: SystemSubGroupId) => {
    setExpandedSubGroups(prev => ({ ...prev, [subGroupId]: !prev[subGroupId] }))
  }, [])

  // Navigate to a route
  const handleNavigate = useCallback(
    (id: string, path: string) => {
      navigate(path)
      onPageChange(id)
    },
    [navigate, onPageChange]
  )

  // Resolve menu items based on navigation mode
  const flatMenuItems = useMemo((): MenuItemConfig[] => {
    const items = getMenuItemsForRole(userRole)
    // Apply badge counts
    return items.map(item => {
      const badge = badgeCounts?.[item.id] ?? item.badge
      return badge !== undefined ? { ...item, badge } : item
    })
  }, [userRole, badgeCounts])

  const groupedMenuItems = useMemo(
    () => NavigationService.getGroupedMenuItemsForRole(userRole, badgeCounts),
    [userRole, badgeCounts]
  )

  // Toggle between grouped and flat navigation (for admin only)
  const handleToggleNavMode = useCallback(() => {
    const newValue = !useGroupedNav
    setUseGroupedNav(newValue)
    setGroupedNavEnabled(newValue)
  }, [useGroupedNav])

  return (
    <Sidebar className="border-r bg-white">
      <SidebarHeader className="border-b p-4">
        <Logo size="lg" showText={true} textSize="lg" className="mb-1" />
        <div>
          <p className="text-xs text-gray-500 mt-1">
            {userRole === 'courier' ? 'Kuryer tətbiqi' : 'Admin paneli'}
          </p>
        </div>

        {/* Admin üçün Role Switcher */}
        {userRole === 'admin' && onRoleChange && (
          <div className="mt-4">
            <Select value={userRole} onValueChange={onRoleChange}>
              <SelectTrigger className="w-full">
                <div className="flex items-center gap-2">
                  <UserCog className="w-4 h-4" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                {Object.entries(roleLabels).map(([role, label]) => (
                  <SelectItem key={role} value={role}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="p-3">
        {/* Conditional rendering based on feature flag */}
        {useGroupedNav ? (
          <GroupedNavigation
            groups={groupedMenuItems}
            currentPage={currentPage}
            expandedGroups={expandedGroups}
            expandedSubGroups={expandedSubGroups}
            onToggleGroup={handleToggleGroup}
            onToggleSubGroup={handleToggleSubGroup}
            onNavigate={handleNavigate}
          />
        ) : (
          <FlatNavigation
            items={flatMenuItems}
            currentPage={currentPage}
            onNavigate={handleNavigate}
          />
        )}

        {/* Quick Actions for Courier */}
        {userRole === 'courier' && (
          <div className="mt-6 p-2">
            <div className="text-xs font-medium text-gray-500 mb-2 px-3">Sürətli əməliyyatlar</div>
            <div className="space-y-1">
              <button
                onClick={() => window.open('https://maps.google.com')}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <Car className="w-4 h-4" />
                <span>Naviqasiya</span>
              </button>
              <button
                onClick={() => window.open('tel:+994125551122')}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Dəstək</span>
              </button>
            </div>
          </div>
        )}

        {/* Navigation mode toggle for admin (dev feature) */}
        {userRole === 'admin' && (
          <div className="mt-4 px-3">
            <button
              onClick={handleToggleNavMode}
              className="w-full text-xs text-gray-400 hover:text-gray-600 py-1"
            >
              {useGroupedNav ? '← Düz menyu' : '→ Qruplar'}
            </button>
          </div>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t p-4">
        <div className="flex items-center gap-3">
          <Avatar className="w-8 h-8">
            <AvatarImage src="/avatar.jpg" />
            <AvatarFallback className="bg-delivery text-white">
              {(roleLabels[userRole] ?? userRole ?? '?')[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{roleLabels[userRole] ?? userRole ?? 'Unknown'}</p>
            <p className="text-xs text-gray-500 truncate">
              {userRole === 'courier' ? 'Növbədə' : 'user@pocketbloom.az'}
            </p>
          </div>
          <button className="p-1 hover:bg-gray-100 rounded">
            <LogOut className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}

// Export feature flag functions for external use
export { getGroupedNavEnabled, setGroupedNavEnabled }

